
import { defineComponent, ref, onMounted, computed, shallowRef } from "vue";
import { IEmailConfig } from "@/interfaces/iEmail";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import VZSideDrawer from "@/components/content/VZSideDrawer.vue";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { usePagination } from "@/helpers/pagination";
import { IBulkDelete } from "@/interfaces/iPageModel";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { TranslationService } from "@/core/services/TranslationService";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import { EmailAPI } from "@/services/api/Email.service";
import ActivityModal from "@/views/user/modal/ActivityModal.vue";
import * as Yup from "yup";
import {
  emailTableBulkDelete,
  emailTableFilters,
  emailTableHeaders,
  EMAIL_TABS,
} from "./emailDataTableOptions";
import { useToast } from "vue-toastification";
import { hasPermission } from "@/core/services/permission.service";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { DrawerComponent } from "@/assets/ts/components";
import CreateEditEmailConfig from "@/views/email/modal/CreateEditEmailConfig.vue";
import { getLookups } from "@/services/api/Lookup.service";
export default defineComponent({
  name: "email-index",
  // eslint-disable-next-line vue/no-unused-components
  components: {
    Datatable,
    VZSideDrawer,
    VZDeleteModal,
    CreateEditEmailConfig,
    ActivityModal,
  },
  setup() {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const datatable = ref<null | HTMLFormElement>(null);
    const showDeleteDialog = ref<any>(false);
    const showEmailAddModal = ref<any>(false);
    const addMessage = t("pages.email.emailConfigAddedMessage");
    const editMessage = t("pages.email.emailConfigUpdatedMessage");
    const { page, total, rowsPerPage } = usePagination();
    const loading = ref(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showSearch = true;
    let options = ref([] as any);
    const typeURL =
      "static-data/filter/LOOKUP_FILTER/filters?filter[lookupName]=LKEmailProvider";
    const sideDrawerTitle = ref<string>("");
    const sideDrawerIcon = ref<string>("");
    const openDrawer = ref<boolean>(false);
    const tableData = ref<IEmailConfig[]>([]);
    const selectedForAction = ref<Partial<IEmailConfig>>({});
    const bulkDelete = ref<IBulkDelete>(emailTableBulkDelete());
    const tableHeader = ref(emailTableHeaders());
    const tableFilter = ref(emailTableFilters());
    const tabs = shallowRef<ITabConfig[]>(EMAIL_TABS());
    const message = t("common.deleteSuccessMessage");
    const showCreateAddModal = ref(false);
    let formData = ref<IAddEditFormModel>({
      header: "Email Configuration",
      edit: false,
      data: null,
      label: [
        { addHeader: t("pages.email.addEmail") },
        { editHeader: t("pages.email.editEmail") },
      ],
      validationSchema: Yup.object({
        name: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required(t("common.name") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.name") + " " + t("common.isRequired")),
        emailProvider: Yup.string().required(
          t("pages.email.provider") + " " + t("common.isRequired")
        ),
        hostName: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("pages.email.hostName"), 30))
          .required(t("pages.email.hostName") + " " + t("common.isRequired"))
          .matches(
            /\S/,
            t("pages.email.hostName") + " " + t("common.isRequired")
          ),
        portNo: Yup.number()
          .max(10, getMaxLengthErrorMessage(t("pages.email.portNumber"), 10))
          .required(t("pages.email.portNumber") + " " + t("common.isRequired")),
        email: Yup.string()
          .email()
          .max(100, getMaxLengthErrorMessage(t("common.email"), 100))
          .matches(
            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
            t("validation.emailValidation")
          )
          .required(t("common.email") + " " + t("common.isRequired")),
        password: Yup.string()
          .nullable()
          .max(30, getMaxLengthErrorMessage(t("common.password"), 30))
          .required(t("common.password")),
        status: Yup.string().required(
          t("common.status") + " " + t("common.isRequired")
        ),
      }),
      model: [
        {
          name: "name",
          label: t("common.name"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "emailProvider",
          label: t("pages.email.provider"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select Provider",
          options: options,
        },
        {
          name: "hostName",
          label: t("pages.email.hostName"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },

        {
          name: "email",
          label: t("common.email"),
          placeholder: "Email@example.com",
          type: "text",
          fieldType: "email",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "portNo",
          label: t("pages.email.portNumber"),
          type: "text",
          fieldType: "number",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "password",
          label: t("common.password"),
          type: "password",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },

        {
          name: "enableSsl",
          label: t("pages.email.sslEnable"),
          type: "checkbox",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "status",
          label: t("common.status"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: true,
          placeholder: "Please select status",
          options: [
            {
              label: t("common.active"),
              value: "Active",
            },
            {
              label: t("common.inactive"),
              value: "Inactive",
            },
          ],
        },
      ],
    });
    let permissionModel = ref<any>({
      create: "VYZOR:INTEGRATIONS:EMAIL MANAGER:CREATE",
      delete: "VYZOR:INTEGRATIONS:EMAIL MANAGER:DELETE",
      update: "VYZOR:INTEGRATIONS:EMAIL MANAGER:UPDATE",
    });
    const tableRowClick = (item) => {
      tabs.value = tabs.value.map((tab) => {
        return { ...tab, props: { ...tab.props, data: item } };
      });
      sideDrawerTitle.value = item?.emailProviderCodeText ?? "";
      sideDrawerIcon.value = "las la-envelope";
      DrawerComponent?.getInstance("vz__drawer")?.toggle();
    };
    const localPreference = ref({});
    const format = computed(() => {
      return FormatMethods;
    });
    const onBulkDelete = async (ids) => {
      let deletedMsg = "Selected email configuration(s) " + message;
      const filteredIds = ids.filter((id) => {
        const foundItem = tableData.value.find((item) => item.webId === id);
        return !(foundItem && foundItem.lock);
      });
      if (filteredIds.length > 0) {
        loading.value = true;
        await EmailAPI.getInstance()
          .deleteEmailConfigs(filteredIds)
          .then(() => {
            toast.success(deletedMsg);
            refreshList();
          })
          .catch(() => {
            //
          })
          .finally(() => {
            loading.value = false;
          });
      } else {
        toast.error("Selected email(s) are locked.");
      }
    };
    onMounted(async () => {
      setCurrentPageBreadcrumbs(t("pages.email.emailConfig.title"), [
        "Home",
        "Email Configuration",
      ]);
      loading.value = true;
      // queryUpdate({
      //   ...defaultFilter.value,
      //   page: page.value - 1,
      //   size: rowsPerPage.value,
      // });
      try {
        const data = await getLookups(typeURL);
        options.value = data?.map((item) => {
          return {
            value: item.value,
            label: item.text,
          };
        });
      } catch (err) {
        console.log(err);
      }
    });

    const queryUpdate = async (params?: Record<string, number | string>) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        localPreference.value = { ...params };
        const data = await EmailAPI.getInstance().getEmailConfigList({
          ...params,
        });
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } finally {
        loading.value = false;
      }
    };

    const editLookup = (item: IEmailConfig) => {
      formData.value.data = { ...item };
      formData.value.edit = true;
      selectedForAction.value = item;
      showCreateAddModal.value = true;
      showEmailAddModal.value = true;
    };

    const submit = async (reqData) => {
      loading.value = true;
      reqData = formData.value.edit ? reqData : { ...reqData };
      let message = formData.value.edit ? editMessage : addMessage;
      await EmailAPI.getInstance()
        .addEmailConfig(reqData, formData.value.edit)
        .then(() => {
          toast.success(message);
          queryUpdate({
            ...localPreference.value,
            page: page.value - 1,
            size: rowsPerPage.value,
          });
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate({ ...localPreference.value });
    };
    const customForm = () => {
      selectedForAction.value = {};
      showCreateAddModal.value = true;
      showEmailAddModal.value = true;
    };
    return {
      bulkDelete,
      format,
      formData,
      tableData,
      tableHeader,
      tableFilter,
      sideDrawerTitle,
      sideDrawerIcon,
      showSearch,
      openDrawer,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      selectedForAction,
      editLookup,
      queryUpdate,
      submit,
      t,
      refreshList,
      onBulkDelete,
      permissionModel,
      hasPermission,
      tableRowClick,
      tabs,
      datatable,
      customForm,
      showCreateAddModal,
      showDeleteDialog,
      showEmailAddModal,
    };
  },
});
